import type { JSMol, RDKitModule } from '@rdkit/rdkit';
import type { DrawingDocument } from '../model';
import { bondStereoValues, isChemicalBond } from '../domain/documentSelectors.ts';
import { haptoLigandsForEngines } from '../domain/haptoBonds.ts';
import { bridgeBondIds, countsBridgesAsHalf } from '../domain/bridgingHydrogens.ts';
import { elementSymbol } from './elements.ts';
import { loadRdKit } from './rdkitAdapter.ts';
import { molecularPieces, type MolecularPiece } from './molecularPieces.ts';

export interface ChemicalSourceRevision {
  readonly structureId: string;
  readonly structureRevision: number;
  readonly depictionId: string;
  readonly depictionRevision: number;
}

export interface RdKitIndexMapping {
  readonly atomIndexByNodeId: Readonly<Record<string, number>>;
  readonly nodeIdByAtomIndex: readonly string[];
  readonly bondIndexByEdgeId: Readonly<Record<string, number>>;
  readonly edgeIdByBondIndex: readonly string[];
}

export interface ChemicalValidationIssue {
  readonly targetType: 'atom' | 'bond' | 'molecule';
  readonly targetId?: string;
  readonly code: 'INVALID_VALENCE' | 'RDKIT_SANITIZATION_FAILED';
  readonly severity: 'error';
  readonly message: string;
}

export interface MolecularProperties {
  /** Null when SMILES can't express the structure: a quintuple or sextuple bond (#1130). */
  readonly canonicalSmiles: string | null;
  readonly molecularFormula: string;
  readonly molecularWeight: number;
  /** #1110: what is on the paper, piece by piece (an ion pair counts as one). */
  readonly pieces: readonly MolecularPiece[];
}

export interface ChemicalValidationResult {
  readonly source: ChemicalSourceRevision;
  readonly sourceKey: string;
  readonly status: 'valid' | 'invalid';
  readonly issues: readonly ChemicalValidationIssue[];
  readonly properties: MolecularProperties | null;
  readonly mapping: RdKitIndexMapping;
  readonly aromaticity: {
    readonly atomIds: readonly string[];
    readonly bondIds: readonly string[];
  };
  readonly stereochemistry: {
    readonly atomValues: Readonly<
      Record<
        string,
        Readonly<{
          parity: 'clockwise' | 'counterclockwise';
          descriptor?: 'R' | 'S';
        }>
      >
    >;
    /** #780: each double bond's CIP label, E or Z -- none for one whose cis/trans is open. */
    readonly bondValues?: Readonly<Record<string, 'E' | 'Z'>>;
  };
  readonly engine: { readonly name: 'RDKit'; readonly version: string };
}

interface RdKitJsonAtom {
  readonly z?: number;
  readonly impHs?: number;
  readonly chg?: number;
  readonly stereo?: string;
}

interface RdKitJson {
  readonly defaults?: { readonly atom?: RdKitJsonAtom };
  readonly molecules?: readonly [
    {
      readonly atoms?: readonly RdKitJsonAtom[];
      readonly bonds?: readonly { readonly atoms: readonly [number, number] }[];
      readonly extensions?: readonly {
        readonly aromaticAtoms?: readonly number[];
        readonly aromaticBonds?: readonly number[];
        readonly cipCodes?: readonly (readonly [number, string])[];
      }[];
    }
  ];
}

const MAX_VALENCE_BY_ATOMIC_NUMBER: Readonly<Record<number, number>> = {
  1: 1,
  5: 3,
  6: 4,
  7: 3,
  8: 2,
  9: 1,
  14: 4,
  15: 5,
  16: 6,
  17: 1,
  33: 5,
  34: 6,
  35: 1,
  53: 1
};

function formatMolCoordinate(value: number): string {
  return value.toFixed(4).padStart(10);
}

export function chemicalSourceRevision(document: DrawingDocument): ChemicalSourceRevision {
  return {
    structureId: document.structure.id,
    structureRevision: document.structure.revision,
    depictionId: document.depiction.id,
    depictionRevision: document.depiction.revision
  };
}

export function chemicalSourceKey(document: DrawingDocument): string {
  // Perceived aromaticity, CIP, and semantic tetrahedral values are outputs here. Key the
  // constitution and wedge input directly so applying perception does not schedule a loop.
  return [
    document.structure.id,
    JSON.stringify(document.structure.graph),
    JSON.stringify(document.structure.atoms),
    JSON.stringify(document.structure.bonds),
    JSON.stringify(document.structure.formalCharges),
    JSON.stringify(bondStereoValues(document))
  ].join(':');
}

export function createRdKitIndexMapping(document: DrawingDocument): RdKitIndexMapping {
  const nodeIdByAtomIndex = document.structure.graph.nodes.map((node) => node.id);
  // ADR 0022: RDKit sees a salt's ions as separate pieces and never sees a hydrogen bond.
  const edgeIdByBondIndex = document.structure.graph.edges
    .filter((edge) => isChemicalBond(document.structure.bonds[edge.id]))
    .map((edge) => edge.id);
  return {
    nodeIdByAtomIndex,
    atomIndexByNodeId: Object.fromEntries(nodeIdByAtomIndex.map((nodeId, index) => [nodeId, index])),
    edgeIdByBondIndex,
    bondIndexByEdgeId: Object.fromEntries(edgeIdByBondIndex.map((edgeId, index) => [edgeId, index]))
  };
}

/**
 * ADR 0024 (#1127): the drawing as RDKit reads it. A molfile, as always -- except for a structure
 * with a bond above triple, which a molfile can't hold (V2000 type 4 means aromatic). That one goes
 * as RDKit's JSON (commonchem), which reads a quadruple bond -- and, #1130, a quintuple or sextuple
 * bond given as quadruple, since RDKit reads neither: the formula, weight, layout and pieces don't
 * depend on it once every atom's hydrogens are given. Only SMILES would be wrong, and is withheld
 * (hasBondAboveQuadruple). JSON takes no implicit hydrogens on
 * trust, so each atom's count is given, from its standard valence (implicitHydrogens). Wedges
 * aren't carried on that route: a structure with a metal–metal quadruple bond is drawn flat.
 */
/** #1130: a bond SMILES can't write -- quintuple or sextuple (ADR 0024 D4). */
export function hasBondAboveQuadruple(document: DrawingDocument): boolean {
  return createRdKitIndexMapping(document).edgeIdByBondIndex.some(
    (edgeId) => (document.structure.bonds[edgeId]?.order ?? 1) > 4
  );
}

export function documentToRdKitInput(drawn: DrawingDocument): string {
  // ADR 0026: an odd hapto ligand drawn with a saturated carbon goes as the radical it binds as.
  const document = haptoLigandsForEngines(drawn);
  const chemicalEdgeIds = createRdKitIndexMapping(document).edgeIdByBondIndex;
  const highOrder = chemicalEdgeIds.some((edgeId) => (document.structure.bonds[edgeId]?.order ?? 1) > 3);
  // ADR 0026 D7: RDKit rejects a bridging hydrogen in a molfile too; JSON gives it zero-order bonds.
  const bridges = bridgeBondIds(document);
  return highOrder || bridges.size > 0 ? documentToRdKitJson(document, bridges) : documentToMolBlock(document);
}

/**
 * ADR 0026 D7: the molecule RDKit lays out (get_new_coords). Given a bridge's bonds as zero-order,
 * its layout loses the ring (diborane came out as a skewed chain), so a structure with a bridging
 * hydrogen is read unsanitized, its bridges as the single bonds they are -- for coordinates only.
 */
export function rdkitMoleculeForLayout(rdkit: RDKitModule, document: DrawingDocument): JSMol | null {
  return bridgeBondIds(document).size > 0
    ? rdkit.get_mol(documentToMolBlock(haptoLigandsForEngines(document)), JSON.stringify({ sanitize: false }))
    : rdkit.get_mol(documentToRdKitInput(document));
}

export function documentToRdKitJson(document: DrawingDocument, bridges: ReadonlySet<string> = new Set()): string {
  const mapping = createRdKitIndexMapping(document);
  // The bonds each atom has, by order (a dative bond counting one on both ends, as its dipolar
  // form below does), for the standard valence's remaining hydrogens. ADR 0026 D7: a bond to a
  // bridging hydrogen counts half on a B, Al or Ga (diborane's B has two terminal hydrogens).
  const bondOrderSum: Record<string, number> = {};
  for (const edgeId of mapping.edgeIdByBondIndex) {
    const edge = document.structure.graph.edges.find((candidate) => candidate.id === edgeId)!;
    const bond = document.structure.bonds[edgeId]!;
    if (bridges.has(edgeId)) {
      for (const end of [edge.source, edge.target]) {
        const half = countsBridgesAsHalf(document.structure.atoms[end]!.atomicNumber);
        bondOrderSum[end] = (bondOrderSum[end] ?? 0) + (half ? 0.5 : 1);
      }
      continue;
    }
    const order = bond.type === 'dative' ? 1 : bond.order;
    bondOrderSum[edge.source] = (bondOrderSum[edge.source] ?? 0) + order;
    bondOrderSum[edge.target] = (bondOrderSum[edge.target] ?? 0) + order;
  }
  // The same dipolar form of a dative bond as the molfile route (B4 #273).
  const dativeCharge: Record<string, number> = {};
  for (const edgeId of mapping.edgeIdByBondIndex) {
    const bond = document.structure.bonds[edgeId]!;
    if (bond.type !== 'dative' || bond.donorAtomId === undefined) continue;
    const edge = document.structure.graph.edges.find((candidate) => candidate.id === edgeId)!;
    const acceptor = bond.donorAtomId === edge.source ? edge.target : edge.source;
    dativeCharge[bond.donorAtomId] = (dativeCharge[bond.donorAtomId] ?? 0) + 1;
    dativeCharge[acceptor] = (dativeCharge[acceptor] ?? 0) - 1;
  }
  const atoms = document.structure.graph.nodes.map((node) => {
    const atom = document.structure.atoms[node.id]!;
    return {
      z: atom.atomicNumber,
      chg: (document.structure.formalCharges[node.id] ?? 0) + (dativeCharge[node.id] ?? 0),
      impHs: implicitHydrogens(
        atom.atomicNumber,
        (document.structure.formalCharges[node.id] ?? 0) + (dativeCharge[node.id] ?? 0),
        (bondOrderSum[node.id] ?? 0) + (atom.radicalElectronCount ?? 0)
      ),
      ...(atom.isotope ? { isotope: atom.isotope } : {}),
      ...(atom.radicalElectronCount ? { nRad: atom.radicalElectronCount } : {})
    };
  });
  const bonds = mapping.edgeIdByBondIndex.map((edgeId) => {
    const edge = document.structure.graph.edges.find((candidate) => candidate.id === edgeId)!;
    const bond = document.structure.bonds[edgeId]!;
    return {
      // ADR 0026 D7: a zero-order bond holds the bridge together without counting as valence.
      bo: bridges.has(edgeId) ? 0 : bond.type === 'dative' ? 1 : Math.min(bond.order, 4),
      atoms: [mapping.atomIndexByNodeId[edge.source]!, mapping.atomIndexByNodeId[edge.target]!]
    };
  });
  const coordinates = document.depiction.coordinateSet.coordinates;
  return JSON.stringify({
    commonchem: { version: 10 },
    defaults: {
      atom: { z: 6, chg: 0, impHs: 0, isotope: 0, nRad: 0, stereo: 'unspecified' },
      bond: { bo: 1, stereo: 'unspecified' }
    },
    molecules: [
      {
        atoms,
        bonds,
        conformers: [
          {
            dim: 2,
            coords: document.structure.graph.nodes.map((node) => {
              const point = coordinates[node.id];
              return point ? [point.x, -point.y] : [0, 0];
            })
          }
        ]
      }
    ]
  });
}

/**
 * The hydrogens an atom takes as RDKit gives them from a molfile -- its standard (lowest) valence,
 * moved by its charge, less its bonds and unpaired electrons. Other elements (metals) take none,
 * as RDKit leaves them. Only the JSON route needs it: JSON takes no hydrogens on trust.
 */
export function implicitHydrogens(atomicNumber: number, charge: number, used: number): number {
  const valence =
    atomicNumber === 6 || atomicNumber === 14
      ? 4 - Math.abs(charge)
      : atomicNumber === 5
        ? 3 - charge
        : [7, 15, 33].includes(atomicNumber)
          ? 3 + charge
          : [8, 16, 34, 52].includes(atomicNumber)
            ? 2 + charge
            : [9, 17, 35, 53].includes(atomicNumber)
              ? 1 + charge
              : 0;
  return Math.max(0, Math.floor(valence - used));
}

export function documentToMolBlock(document: DrawingDocument): string {
  const coordinateSet = document.depiction.coordinateSet;
  const mapping = createRdKitIndexMapping(document);
  const chemicalEdges = mapping.edgeIdByBondIndex.map((edgeId) =>
    document.structure.graph.edges.find((edge) => edge.id === edgeId)!
  );
  const counts =
    document.structure.graph.nodes.length.toString().padStart(3) + chemicalEdges.length.toString().padStart(3);
  const atoms = document.structure.graph.nodes.map((node) => {
    const coordinate = coordinateSet.coordinates[node.id]!;
    const atomicNumber = document.structure.atoms[node.id]!.atomicNumber;
    return (
      formatMolCoordinate(coordinate.x) +
      formatMolCoordinate(-coordinate.y) +
      formatMolCoordinate(0) +
      ' ' +
      elementSymbol(atomicNumber).padEnd(3) +
      ' 0  0  0  0  0  0  0  0  0  0  0  0'
    );
  });
  const bonds = chemicalEdges.map((edge) => {
    const stereo = bondStereoValues(document)[edge.id];
    const sourceNodeId = stereo?.type === 'wedge' || stereo?.type === 'wavy' ? stereo.fromNodeId : edge.source;
    const targetNodeId = sourceNodeId === edge.source ? edge.target : edge.source;
    const source = mapping.atomIndexByNodeId[sourceNodeId]! + 1;
    const target = mapping.atomIndexByNodeId[targetNodeId]! + 1;
    const order = document.structure.bonds[edge.id]!.order;
    // ADR 0022 D5: a wavy bond is V2000 stereo 4, "either" -- RDKit leaves its centre unspecified.
    // A crossed double bond is 3, "either cis or trans" (#1059): without it, RDKit read the drawn
    // layout as one or the other.
    const stereoCode =
      stereo?.type === 'wedge'
        ? stereo.style === 'solid'
          ? 1
          : 6
        : stereo?.type === 'wavy'
          ? 4
          : stereo?.type === 'crossed-double' && order === 2
            ? 3
            : 0;
    return (
      source.toString().padStart(3) +
      target.toString().padStart(3) +
      order.toString().padStart(3) +
      stereoCode.toString().padStart(3) +
      '  0  0  0'
    );
  });
  // V2000 has no dative bond type, and RDKit would reject the neutral donor's raised valence
  // (e.g. tetravalent neutral N). Feed RDKit the dipolar resonance form instead: the dative bond
  // is written as an order-1 covalent bond (above) with +1 on the donor and -1 on the acceptor,
  // which sanitizes cleanly. This only shapes the RDKit input -- the document's own formal
  // charges and rendering are untouched (B4 #273).
  const dativeChargeByNodeId: Record<string, number> = {};
  for (const edge of document.structure.graph.edges) {
    const bond = document.structure.bonds[edge.id]!;
    if (bond.type !== 'dative' || bond.donorAtomId === undefined) continue;
    const donor = bond.donorAtomId;
    const acceptor = donor === edge.source ? edge.target : edge.source;
    dativeChargeByNodeId[donor] = (dativeChargeByNodeId[donor] ?? 0) + 1;
    dativeChargeByNodeId[acceptor] = (dativeChargeByNodeId[acceptor] ?? 0) - 1;
  }
  const chargedAtoms = document.structure.graph.nodes.flatMap((node, index) => {
    const formalCharge = document.structure.formalCharges[node.id]! + (dativeChargeByNodeId[node.id] ?? 0);
    return formalCharge === 0 ? [] : [{ atomIndex: index + 1, formalCharge }];
  });
  const chargeLines: string[] = [];
  for (let index = 0; index < chargedAtoms.length; index += 8) {
    const entries = chargedAtoms.slice(index, index + 8);
    chargeLines.push(
      'M  CHG' +
        entries.length.toString().padStart(3) +
        entries
          .map((entry) => entry.atomIndex.toString().padStart(4) + entry.formalCharge.toString().padStart(4))
          .join('')
    );
  }
  const isotopicAtoms = document.structure.graph.nodes.flatMap((node, index) => {
    const isotope = document.structure.atoms[node.id]!.isotope;
    return isotope === undefined ? [] : [{ atomIndex: index + 1, isotope }];
  });
  const isotopeLines: string[] = [];
  for (let index = 0; index < isotopicAtoms.length; index += 8) {
    const entries = isotopicAtoms.slice(index, index + 8);
    isotopeLines.push(
      'M  ISO' +
        entries.length.toString().padStart(3) +
        entries.map((entry) => entry.atomIndex.toString().padStart(4) + entry.isotope.toString().padStart(4)).join('')
    );
  }
  // MDL RAD property values are spin multiplicities (1 singlet, 2 doublet, 3 triplet), not raw
  // radical-electron counts. One unpaired electron is a doublet; two is a triplet. Three has no
  // standard RAD code and falls back to triplet, matching RDKit's own molfile writer.
  const radicalAtoms = document.structure.graph.nodes.flatMap((node, index) => {
    const radicalElectronCount = document.structure.atoms[node.id]!.radicalElectronCount;
    return radicalElectronCount === undefined
      ? []
      : [{ atomIndex: index + 1, radicalCode: radicalElectronCount === 1 ? 2 : 3 }];
  });
  const radicalLines: string[] = [];
  for (let index = 0; index < radicalAtoms.length; index += 8) {
    const entries = radicalAtoms.slice(index, index + 8);
    radicalLines.push(
      'M  RAD' +
        entries.length.toString().padStart(3) +
        entries
          .map((entry) => entry.atomIndex.toString().padStart(4) + entry.radicalCode.toString().padStart(4))
          .join('')
    );
  }
  return [
    'Element layered molecule',
    '  PeruseLab 2D',
    '',
    counts + '  0  0  0  0            999 V2000',
    ...atoms,
    ...bonds,
    ...chargeLines,
    ...isotopeLines,
    ...radicalLines,
    'M  END',
    ''
  ].join('\n');
}

// Nitrogen, oxygen, and the halogens each gain one bonding slot per positive charge -- N+ 4,
// O+ 3, Cl+ 2 -- so a materialised Rule 4.4.1 auto-charge keeps the atom valid. Every other
// element uses its neutral ceiling regardless of charge.
const CATION_GAINS_A_SLOT = new Set([7, 8, 9, 17, 35, 53]);

// #1037: boron is a Lewis acid -- a dative bond it accepts fills its empty orbital rather than
// spending its valence (H3N->BH3's boron has 4 bonds, as BH4-). Each accepted dative bond adds a
// slot. An N / O acceptor gets none, so an impossible O->NR3 stays flagged.
const DATIVE_ACCEPTOR_GAINS_A_SLOT = new Set([5]);

export function maximumSupportedValence(atomicNumber: number, formalCharge: number): number | undefined {
  const base = MAX_VALENCE_BY_ATOMIC_NUMBER[atomicNumber];
  if (base === undefined) return undefined;
  return formalCharge > 0 && CATION_GAINS_A_SLOT.has(atomicNumber) ? base + formalCharge : base;
}

function findValenceIssues(document: DrawingDocument): readonly ChemicalValidationIssue[] {
  const valenceByNodeId: Record<string, number> = Object.fromEntries(
    document.structure.graph.nodes.map((node) => [node.id, 0])
  );
  const acceptedDativeByNodeId: Record<string, number> = {};
  // ADR 0026 D7: each bond of a three-centre bridge counts half -- on its hydrogen (two halves make
  // one) and on the atoms it bridges (diborane's B, with two terminal hydrogens, makes three).
  const bridges = bridgeBondIds(document);
  for (const edge of document.structure.graph.edges) {
    const bond = document.structure.bonds[edge.id]!;
    const order = bridges.has(edge.id) ? 0.5 : bond.order;
    // A dative bond counts against the acceptor's valence only; the donor contributes a lone pair
    // and its valence is unaffected (RDKit BondType.DATIVE; B4 #273).
    const donor = bond.type === 'dative' ? bond.donorAtomId : undefined;
    if (edge.source !== donor) valenceByNodeId[edge.source] = (valenceByNodeId[edge.source] ?? 0) + order;
    if (edge.target !== donor) valenceByNodeId[edge.target] = (valenceByNodeId[edge.target] ?? 0) + order;
    if (donor !== undefined) {
      const acceptor = donor === edge.source ? edge.target : edge.source;
      acceptedDativeByNodeId[acceptor] = (acceptedDativeByNodeId[acceptor] ?? 0) + 1;
    }
  }
  return document.structure.graph.nodes.flatMap((node) => {
    const atomicNumber = document.structure.atoms[node.id]!.atomicNumber;
    const formalCharge = document.structure.formalCharges[node.id]!;
    const ceiling = maximumSupportedValence(atomicNumber, formalCharge);
    const maximum =
      ceiling !== undefined && DATIVE_ACCEPTOR_GAINS_A_SLOT.has(atomicNumber)
        ? ceiling + (acceptedDativeByNodeId[node.id] ?? 0)
        : ceiling;
    const actual = valenceByNodeId[node.id] ?? 0;
    if (maximum === undefined || actual <= maximum) return [];
    return [
      {
        targetType: 'atom' as const,
        targetId: node.id,
        code: 'INVALID_VALENCE' as const,
        severity: 'error' as const,
        message: `${elementSymbol(atomicNumber)} has bond order ${actual}; maximum supported valence is ${maximum}.`
      }
    ];
  });
}

export interface FragmentFormula {
  readonly formula: string;
  /** Atoms including hydrogens: "largest first" puts H2O ahead of a lone Na+. */
  readonly atoms: number;
  readonly charge: number;
}

/** #1110: the formula, size and charge of a molecule read as one piece. */
export function pieceFormula(molecule: JSMol): FragmentFormula {
  const json = JSON.parse(molecule.get_json()) as RdKitJson;
  return fragmentFormula(json.molecules?.[0]?.atoms ?? [], json.defaults?.atom);
}

// #1073: one formula per fragment, joined with " · " (the InChI/SMILES convention for several
// components) -- a sum of every piece on the paper is the formula of no substance shown. Largest
// fragment first, identical fragments counted once ("2 CH4"), each keeping its own charge: ions are
// not merged into a salt formula, since telling a salt from unrelated ions would be a guess.
// ADR 0026: `joins` are atom-index pairs a hapto bond holds together, so a complex is one formula.
export function molecularFormula(molecule: JSMol, joins: readonly (readonly [number, number])[] = []): string {
  const json = JSON.parse(molecule.get_json()) as RdKitJson;
  const defaults = json.defaults?.atom;
  const atoms = json.molecules?.[0]?.atoms ?? [];
  const fragmentOf = connectedFragments(atoms.length, [
    ...(json.molecules?.[0]?.bonds ?? []),
    ...joins.map((atoms) => ({ atoms }))
  ]);
  const fragments = new Map<number, RdKitJsonAtom[]>();
  atoms.forEach((atom, index) => {
    const root = fragmentOf[index]!;
    fragments.set(root, [...(fragments.get(root) ?? []), atom]);
  });
  const counted = new Map<string, { fragment: FragmentFormula; count: number }>();
  for (const fragmentAtoms of fragments.values()) {
    const fragment = fragmentFormula(fragmentAtoms, defaults);
    const entry = counted.get(fragment.formula);
    if (entry) entry.count += 1;
    else counted.set(fragment.formula, { fragment, count: 1 });
  }
  return [...counted.values()]
    .sort(
      (first, second) =>
        second.fragment.atoms - first.fragment.atoms || first.fragment.formula.localeCompare(second.fragment.formula)
    )
    .map(({ fragment, count }) => (count === 1 ? fragment.formula : `${count} ${fragment.formula}`))
    .join(' · ');
}

/** Each atom index mapped to a representative atom of its connected fragment. */
function connectedFragments(
  atomCount: number,
  bonds: readonly { readonly atoms: readonly [number, number] }[]
): number[] {
  const parent = Array.from({ length: atomCount }, (_, index) => index);
  const find = (index: number): number => {
    while (parent[index] !== index) index = parent[index] = parent[parent[index]!]!;
    return index;
  };
  for (const { atoms } of bonds) parent[find(atoms[0])] = find(atoms[1]);
  return parent.map((_, index) => find(index));
}

function fragmentFormula(atoms: readonly RdKitJsonAtom[], defaults: RdKitJsonAtom | undefined): FragmentFormula {
  const counts = new Map<string, number>();
  let formalCharge = 0;
  for (const atom of atoms) {
    const symbol = elementSymbol(atom.z ?? defaults?.z ?? 6);
    counts.set(symbol, (counts.get(symbol) ?? 0) + 1);
    const hydrogens = atom.impHs ?? defaults?.impHs ?? 0;
    if (hydrogens > 0) counts.set('H', (counts.get('H') ?? 0) + hydrogens);
    formalCharge += atom.chg ?? defaults?.chg ?? 0;
  }
  const orderedSymbols = [
    ...(counts.has('C') ? ['C'] : []),
    ...(counts.has('H') ? ['H'] : []),
    ...[...counts.keys()]
      .filter((symbol) => symbol !== 'C' && symbol !== 'H')
      .sort((first, second) => first.localeCompare(second))
  ];
  const formula = orderedSymbols
    .map((symbol) => {
      const count = counts.get(symbol) ?? 0;
      return symbol + (count === 1 ? '' : count);
    })
    .join('');
  const size = [...counts.values()].reduce((sum, count) => sum + count, 0);
  if (formalCharge === 0) return { formula, atoms: size, charge: 0 };
  // Sign before magnitude, as PubChem writes it ("Ca+2"): "Ca2+" would read the same as a Ca2 with
  // charge +1, so the display could not tell which digits are a count.
  const magnitude = Math.abs(formalCharge);
  return {
    formula: formula + (formalCharge > 0 ? '+' : '-') + (magnitude === 1 ? '' : magnitude),
    atoms: size,
    charge: formalCharge
  };
}

export function molecularWeight(molecule: JSMol): number {
  const descriptors = JSON.parse(molecule.get_descriptors()) as { readonly amw?: number };
  if (!Number.isFinite(descriptors.amw)) throw new Error('RDKit did not return molecular weight.');
  return descriptors.amw!;
}

/**
 * ADR 0026: the atom-index pairs each hapto bond holds together -- its metal and every ligand
 * atom. RDKit never sees the bond, so its pieces are the metal and the ligands apart.
 */
export function haptoJoins(document: DrawingDocument, mapping: RdKitIndexMapping): [number, number][] {
  return document.structure.graph.edges.flatMap((edge) => {
    const ligand = document.structure.bonds[edge.id]?.ligandAtomIds;
    const metal = mapping.atomIndexByNodeId[edge.source];
    if (!ligand || metal === undefined) return [];
    return ligand.flatMap((id): [number, number][] => {
      const index = mapping.atomIndexByNodeId[id];
      return index === undefined ? [] : [[metal, index]];
    });
  });
}

/**
 * ADR 0026 (#1156): the pieces of a drawing with hapto bonds -- each complex one piece (its formula
 * from all its atoms; its SMILES its pieces, as SMILES can't write a hapto bond), the rest as
 * molecularPieces makes them. Each part is cut from RDKit's own reading of the drawing.
 */
function haptoAwarePieces(
  rdkit: RDKitModule,
  molecule: JSMol,
  joins: readonly (readonly [number, number])[]
): readonly MolecularPiece[] {
  const json = JSON.parse(molecule.get_json()) as RdKitJson & Record<string, unknown>;
  const atoms = json.molecules?.[0]?.atoms ?? [];
  const bonds = json.molecules?.[0]?.bonds ?? [];
  const fragmentOf = connectedFragments(atoms.length, [...bonds, ...joins.map((pair) => ({ atoms: pair }))]);
  const complexRoots = new Set(joins.map(([metal]) => fragmentOf[metal]!));
  const cut = (indexes: readonly number[]): JSMol | null => {
    const at = new Map(indexes.map((index, position) => [index, position]));
    const part = {
      ...json,
      molecules: [
        {
          atoms: indexes.map((index) => atoms[index]!),
          bonds: bonds
            .filter(({ atoms: [a, b] }) => at.has(a) && at.has(b))
            .map((bond) => ({ ...bond, atoms: [at.get(bond.atoms[0])!, at.get(bond.atoms[1])!] }))
        }
      ]
    };
    return rdkit.get_mol(JSON.stringify(part));
  };
  const pieces: MolecularPiece[] = [];
  for (const root of complexRoots) {
    const indexes = atoms.flatMap((_, index) => (fragmentOf[index] === root ? [index] : []));
    const part = cut(indexes);
    try {
      if (!part?.is_valid()) continue;
      pieces.push({
        formula: fragmentFormula(
          indexes.map((index) => atoms[index]!),
          json.defaults?.atom
        ).formula,
        smiles: part.get_smiles(),
        molecularWeight: molecularWeight(part),
        count: 1,
        ionPair: false,
        hapto: true
      });
    } finally {
      part?.delete();
    }
  }
  const rest = atoms.flatMap((_, index) => (complexRoots.has(fragmentOf[index]!) ? [] : [index]));
  if (rest.length === 0) return pieces;
  const part = cut(rest);
  try {
    return part?.is_valid() ? [...pieces, ...molecularPieces(rdkit, part.get_smiles())] : pieces;
  } finally {
    part?.delete();
  }
}

function propertiesFromMolecule(
  rdkit: RDKitModule,
  molecule: JSMol,
  smilesWritable: boolean,
  joins: readonly (readonly [number, number])[] = [],
  bridged = false
): MolecularProperties {
  // #1130: with a quintuple or sextuple bond RDKit read it as quadruple -- the formula, weight and
  // pieces hold, the SMILES doesn't, so none is given (ADR 0024 D4).
  const smiles = molecule.get_smiles();
  const pieces = joins.length ? haptoAwarePieces(rdkit, molecule, joins) : molecularPieces(rdkit, smiles);
  // ADR 0026 D7: RDKit writes a bridge's zero-order bonds as "~"; as single bonds the SMILES is
  // PubChem's ([H]1[BH2][H][BH2]1). Only bridges are given RDKit as zero-order, so only they read "~".
  const written = (text: string) => (bridged ? text.replaceAll('~', '') : text);
  return {
    canonicalSmiles: smilesWritable ? written(smiles) : null,
    molecularFormula: molecularFormula(molecule, joins),
    molecularWeight: molecularWeight(molecule),
    pieces: pieces.map((piece) => ({ ...piece, smiles: smilesWritable && piece.smiles ? written(piece.smiles) : null }))
  };
}

function aromaticityFromMolecule(molecule: JSMol, mapping: RdKitIndexMapping): ChemicalValidationResult['aromaticity'] {
  const json = JSON.parse(molecule.get_json()) as RdKitJson;
  const extensions = json.molecules?.[0]?.extensions ?? [];
  const atomIndexes = new Set(extensions.flatMap((extension) => extension.aromaticAtoms ?? []));
  const bondIndexes = new Set(extensions.flatMap((extension) => extension.aromaticBonds ?? []));
  return {
    atomIds: mapping.nodeIdByAtomIndex.filter((_, index) => atomIndexes.has(index)),
    bondIds: mapping.edgeIdByBondIndex.filter((_, index) => bondIndexes.has(index))
  };
}

function stereochemistryFromMolecule(
  molecule: JSMol,
  mapping: RdKitIndexMapping,
  document: DrawingDocument
): ChemicalValidationResult['stereochemistry'] {
  const json = JSON.parse(molecule.get_json()) as RdKitJson;
  const parsed = json.molecules?.[0];
  const cipCodes = new Map(parsed?.extensions?.flatMap((extension) => extension.cipCodes ?? []) ?? []);
  const atomValues = Object.fromEntries(
    (parsed?.atoms ?? []).flatMap((atom, index) => {
      const stereo = atom.stereo ?? json.defaults?.atom?.stereo ?? 'unspecified';
      if (stereo !== 'cw' && stereo !== 'ccw') return [];
      const descriptor = cipCodes.get(index);
      return [
        [
          mapping.nodeIdByAtomIndex[index]!,
          {
            parity: stereo === 'cw' ? ('clockwise' as const) : ('counterclockwise' as const),
            ...(['R', 'S'].includes(descriptor ?? '') ? { descriptor: descriptor as 'R' | 'S' } : {})
          }
        ]
      ];
    })
  );
  return { atomValues, bondValues: bondCipLabels(molecule, mapping, document) };
}

/**
 * #780: RDKit's CIP label for each double bond with a defined configuration -- E or Z by the
 * substituents' priorities, not plain cis/trans -- keyed by the drawing's edge. A crossed or open
 * double bond has none.
 */
function bondCipLabels(
  molecule: JSMol,
  mapping: RdKitIndexMapping,
  document: DrawingDocument
): Record<string, 'E' | 'Z'> {
  let tags: { CIP_bonds?: [number, number, string][] };
  try {
    tags = JSON.parse((molecule as unknown as { get_stereo_tags(): string }).get_stereo_tags()) as typeof tags;
  } catch {
    return {};
  }
  const labels: Record<string, 'E' | 'Z'> = {};
  for (const [first, second, label] of tags.CIP_bonds ?? []) {
    const ends = [mapping.nodeIdByAtomIndex[first], mapping.nodeIdByAtomIndex[second]];
    const edgeId = mapping.edgeIdByBondIndex.find((id) => {
      const edge = document.structure.graph.edges.find((candidate) => candidate.id === id);
      return edge !== undefined && ends.includes(edge.source) && ends.includes(edge.target);
    });
    const descriptor = label.replace(/[()]/g, '');
    if (edgeId && (descriptor === 'E' || descriptor === 'Z')) labels[edgeId] = descriptor;
  }
  return labels;
}

export async function validateChemicalDocument(document: DrawingDocument): Promise<ChemicalValidationResult> {
  const source = chemicalSourceRevision(document);
  const sourceKey = chemicalSourceKey(document);
  const mapping = createRdKitIndexMapping(document);
  const issues = [...findValenceIssues(document)];
  const rdkit = await loadRdKit();
  let molecule: JSMol | null = null;
  try {
    molecule = rdkit.get_mol(documentToRdKitInput(document));
    if (!molecule || !molecule.is_valid()) {
      if (issues.length === 0)
        issues.push({
          targetType: 'molecule',
          code: 'RDKIT_SANITIZATION_FAILED',
          severity: 'error',
          message: 'RDKit could not sanitize the graph and chemical layers.'
        });
      return {
        source,
        sourceKey,
        status: 'invalid',
        issues,
        properties: null,
        mapping,
        aromaticity: { atomIds: [], bondIds: [] },
        stereochemistry: { atomValues: {} },
        engine: { name: 'RDKit', version: rdkit.version() }
      };
    }
    return {
      source,
      sourceKey,
      status: issues.length === 0 ? 'valid' : 'invalid',
      issues,
      properties:
        issues.length === 0
          ? propertiesFromMolecule(
              rdkit,
              molecule,
              !hasBondAboveQuadruple(document),
              haptoJoins(document, mapping),
              bridgeBondIds(document).size > 0
            )
          : null,
      mapping,
      aromaticity: issues.length === 0 ? aromaticityFromMolecule(molecule, mapping) : { atomIds: [], bondIds: [] },
      stereochemistry:
        issues.length === 0 ? stereochemistryFromMolecule(molecule, mapping, document) : { atomValues: {} },
      engine: { name: 'RDKit', version: rdkit.version() }
    };
  } catch (error) {
    return {
      source,
      sourceKey,
      status: 'invalid',
      issues:
        issues.length > 0
          ? issues
          : [
              {
                targetType: 'molecule',
                code: 'RDKIT_SANITIZATION_FAILED',
                severity: 'error',
                message: error instanceof Error ? error.message : 'RDKit validation failed.'
              }
            ],
      properties: null,
      mapping,
      aromaticity: { atomIds: [], bondIds: [] },
      stereochemistry: { atomValues: {} },
      engine: { name: 'RDKit', version: rdkit.version() }
    };
  } finally {
    molecule?.delete();
  }
}
